import type { Theme } from '@material-ui/core'
import { createStyles, makeStyles } from '@material-ui/core'
import { getFeature, getObjectLabels, getQuoteTypeOptions } from '@paintscout/util/builder'
import { default as React, useState } from 'react'
import type { DialogProps } from '@paintscout/ui'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, InputField } from '@paintscout/ui'
import type { DropdownSelectOption } from '../components/DropdownSelect'
import DropdownSelect from '../components/DropdownSelect'
import { useClientOptions } from '../providers/ClientOptionsProvider'
import { FollowUpStep } from './FollowUpDialog'

export interface CreateTemplateDialogProps extends DialogProps {
  existingName?: string
  existingType?: string
  existingTypes?: string[]
  onConfirm: (quoteTypes: string[], templatename: string) => void
  onCancel: () => void
}

export const useStyles = makeStyles((_theme: Theme) => {
  return createStyles({
    form: {
      display: 'block'
    },
    templateNameField: {},
    dialogContent: {}
  })
})

function CreateTemplateDialog({
  existingName = '',
  existingTypes = [],
  onCancel,
  onConfirm,
  ...baseDialogProps
}: CreateTemplateDialogProps) {
  const classes = useStyles({})
  const { options } = useClientOptions()

  const [types, setTypes] = useState<string[]>(existingTypes.filter((i) => i !== '__all__'))
  const [type, setType] = useState<DropdownSelectOption>(null)
  const [templateName, setTemplateName] = useState(existingName ? existingName + ' (copy)' : '')
  const multiQuoteTemplates = getFeature({ options, path: 'quotes.multiQuoteTemplates' })
  const quoteTypeOptions = getQuoteTypeOptions({ options })
  const objectLabels = getObjectLabels({ options })

  const handleSubmit = (e) => {
    e.preventDefault()
    if (multiQuoteTemplates) {
      onConfirm(types, templateName)
    } else {
      onConfirm([type.value], templateName)
    }
  }

  return (
    <Dialog open={open} onClose={onCancel} maxWidth={'md'} fullWidth={true} {...baseDialogProps}>
      <DialogTitle>{`Create New ${objectLabels.quote.value} Template`}</DialogTitle>
      <DialogContent classes={{ root: classes.dialogContent }}>
        <form id="create-template-dialog-form" onSubmit={handleSubmit} className={classes.form}>
          <FollowUpStep step={1} totalSteps={2} subtitle="Name your template.">
            <>
              <InputField
                className={classes.templateNameField}
                required
                label="Name"
                value={templateName}
                onChange={(e) => setTemplateName(e.currentTarget.value)}
                fullWidth
                margin="dense"
                autoFocus
                data-testid="name-input"
                error={!templateName}
              />
              <hr style={{ border: '#fff' }} />
            </>
          </FollowUpStep>
          <FollowUpStep
            step={2}
            totalSteps={2}
            subtitle={`Select which ${objectLabels.quote.value.toLowerCase()} ${
              multiQuoteTemplates ? 'types' : 'type'
            } this template is assigned to.`}
            disabled={!templateName}
          >
            {multiQuoteTemplates ? (
              <DropdownSelect
                name="type"
                variant="multi"
                label={`${objectLabels.quote.value} Types`}
                placeholder={{
                  selectPlaceholder: 'Select Types',
                  plural: 'Types'
                }}
                options={quoteTypeOptions.map((option) => {
                  return {
                    value: option.key,
                    label: option.key === '__all__' ? 'All Types' : option.label
                  }
                })}
                required
                error={!types.length}
                searchable
                value={
                  types.length
                    ? types.map((type) => {
                        return {
                          value: type,
                          label: quoteTypeOptions.find((option) => option.key === type).label
                        }
                      })
                    : []
                }
                onChange={(newTypes: DropdownSelectOption[]) => setTypes(newTypes.map((newType) => newType.value))}
                fullWidth
                disabled={!templateName}
              />
            ) : (
              <DropdownSelect
                name="type"
                variant="single"
                label={`${objectLabels.quote.value} Type`}
                placeholder={{
                  selectPlaceholder: 'Select Type',
                  plural: 'Types'
                }}
                options={quoteTypeOptions.map((option) => {
                  return {
                    value: option.key,
                    label: option.key === '__all__' ? 'All Types' : option.label
                  }
                })}
                required
                error={!type}
                searchable
                value={type}
                onChange={setType}
                fullWidth
                disabled={!templateName}
              />
            )}
          </FollowUpStep>
        </form>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button type="submit" form="create-template-dialog-form">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateTemplateDialog
